import type { ApiClientErrorCode, FurnitureListItem } from '@pomoshnik/shared';
import { Header } from '@/components/Header/Header';
import { BottomBar } from '@/components/BottomBar/BottomBar';

/**
 * Presenter Экрана 2.5.7 «Фурнитура и магазины — поиск» (ADR-0068, ADR-0005).
 *
 * Чистая функция props → JSX. Поля строго по порядку (прототип/PRD): Бренд
 * (необязательно, select из 5 кодов; показ brandName) + Название (input,
 * автокомплит-подстрока). 5 состояний (P-021): idle (скоуп-список) / loading /
 * success (совпадения) / empty / error. Layout shell P-046, вертикальные
 * подсказки P-019, поле без языкопереключателя P-020, токены P-038…P-048.
 *
 * P-040: VideoButton тут НЕ дублируется — это дочерний экран входа блока (2.5.6
 * FurnitureEntry, где VideoButton первым). Прецедент — DecorsSearchView (дочерний
 * экран метод-пикера). Прототип показывает видеокнопку и на поиске — расхождение
 * прототип↔дизайн-система решено в пользу P-040 (на визуальную сверку Aibek).
 */
export type FurnitureSearchState = 'idle' | 'loading' | 'success' | 'empty' | 'error';

/** Опция бренда: код (для матча/value) + читаемое имя (показ). */
export interface FurnitureBrandOption {
  code: string;
  brandName: string;
}

export interface FurnitureSearchViewProps {
  state: FurnitureSearchState;
  brandOptions: readonly FurnitureBrandOption[];
  /** Выбранный код бренда ('' = все бренды). */
  brand: string;
  onBrandChange: (code: string) => void;
  query: string;
  onQueryChange: (next: string) => void;
  items?: readonly FurnitureListItem[];
  error?: { code: ApiClientErrorCode; message: string };
  onItemClick: (globalId: string) => void;
}

export function FurnitureSearchView(props: FurnitureSearchViewProps) {
  return (
    <main className="min-h-screen flex flex-col bg-canvas">
      <Header title="Фурнитура и магазины" />

      <section className="flex-1 overflow-y-auto flex flex-col gap-4 p-6">
        {/* Бренд (необязательно) */}
        <div className="flex flex-col gap-2">
          <label htmlFor="furniture-brand" className="text-ink text-base font-body font-semibold">
            Бренд (необязательно)
          </label>
          <select
            id="furniture-brand"
            value={props.brand}
            onChange={(e) => props.onBrandChange(e.target.value)}
            aria-label="Бренд"
            className={[
              'w-full min-h-tap rounded-lg bg-card border-[1.5px] border-accent text-ink',
              'text-[18px] px-4 py-3',
              'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none',
            ].join(' ')}
          >
            <option value="">Все бренды</option>
            {props.brandOptions.map((o) => (
              <option key={o.code} value={o.code}>
                {o.brandName}
              </option>
            ))}
          </select>
        </div>

        {/* Название фурнитуры */}
        <div className="flex flex-col gap-2">
          <label htmlFor="furniture-name" className="text-ink text-base font-body font-semibold">
            Название фурнитуры
          </label>
          <input
            id="furniture-name"
            type="text"
            inputMode="search"
            autoComplete="off"
            value={props.query}
            onChange={(e) => props.onQueryChange(e.target.value)}
            placeholder="Например: бутылочница, направляющие, петля…"
            aria-label="Название фурнитуры"
            className={[
              'w-full min-h-tap rounded-lg bg-card border-[1.5px] border-accent text-ink',
              'text-[18px] px-4 py-3 placeholder:text-muted',
              'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none',
            ].join(' ')}
          />
        </div>

        <StateView {...props} />
      </section>

      <BottomBar />
    </main>
  );
}

function StateView(props: FurnitureSearchViewProps) {
  switch (props.state) {
    case 'loading':
      return <Centered text="Загружаем…" tone="muted" />;
    case 'empty':
      return <Centered text="Ничего не нашли. Попробуйте описать иначе." tone="muted" />;
    case 'error':
      return (
        <Centered
          text="Не удалось получить ответ. Проверьте интернет и попробуйте снова."
          tone="error"
        />
      );
    case 'idle':
    case 'success':
      return <SuggestionList {...props} />;
  }
}

function SuggestionList(props: FurnitureSearchViewProps) {
  const items = props.items ?? [];
  if (items.length === 0) {
    // idle без данных в скоупе (редко) — нейтральная подсказка.
    return <Centered text="Выберите товар или начните вводить название." tone="muted" />;
  }
  return (
    <ul className="flex flex-col gap-3 list-none m-0 p-0">
      {items.map((item) => (
        <li key={item.globalId}>
          {/* Текстовая строка (решение Aibek поверх прототипа: без мини-картинки). */}
          <button
            type="button"
            onClick={() => props.onItemClick(item.globalId)}
            aria-label={item.name}
            className={[
              'w-full min-h-tap flex flex-col rounded-lg bg-card border-[1.5px] border-accent',
              'text-left px-3 py-2 shadow-card',
              'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
            ].join(' ')}
          >
            <span className="text-ink text-base font-body font-semibold truncate">{item.name}</span>
            <span className="text-muted text-sm truncate">
              {item.brandName} · {item.subcategory}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function Centered({ text, tone }: { text: string; tone: 'muted' | 'error' }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[30vh] gap-3 text-center">
      <p
        className={[
          tone === 'error' ? 'text-error' : 'text-muted',
          'text-[18px] max-w-[300px] m-0 leading-normal',
        ].join(' ')}
      >
        {text}
      </p>
    </div>
  );
}
